import React, { useEffect, useMemo } from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';
import { sortBy } from 'lodash';
import { getStringWithoutHash } from 'utilitys/helpers/general';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';

import { useQueryPortfolioTopHeroes } from 'api/queries/getPortfolioTopHeroes.query';
import { IPortfolioTopHeroesItems } from 'api/queries/getProjectScoreCardsSummary';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { CardItem } from 'components/common/WidgetWithCards/WidgetCard/CardItem/CardItem';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import classes from 'components/Dashboards/Portfolio/PortfolioOverview/PortfolioOverview.module.scss';
import {
    capitalizeFirstLetter,
    getCardItemValue,
    getColorFromStatus,
    serializeoverAllStatusData,
} from 'components/Dashboards/Portfolio/PortfolioOverview/helper';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IPortfolioProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer, WidgetCard } from 'components/common';
import { CardItemProgressBar } from 'components/widgets/OverallStatus/components/CardItemProgressBar';

const overallStatusId = 'overall-status';

export const OverallStatus = ({
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    filter,
    widgetViewMode = WidgetView.page,
}: IPortfolioProps) => {
    const globalFilterData = useGlobalFilterDataStore((state) => state.globalFilterData);
    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters || []
                : getFiltersData({
                      [FilterNames.TAG_PORTFOLIO_BOARD]: globalFilterData?.programTags?.map((item) => item.value),
                  }),

        [filter, globalFilterData],
    );
    const {
        data: overAllStatus,
        isLoading: isLoadingoverAllStatus,
        isFetching: isFetchingoverAllStatus,
    } = useQueryPortfolioTopHeroes({
        filter: {
            filters,
        },
    });

    const subTitle = (
        <>
            Total <span className={classes.totalCount}>{overAllStatus?.numOfItems ?? 0}</span> projects/sub-programs
        </>
    );

    const overAllStatusData = serializeoverAllStatusData({ data: overAllStatus?.componentsList });
    const route = `${getStringWithoutHash(window.location.href)}#${overallStatusId}`;
    const componentKey: WidgetsMapComponentKeys = 'overallStatus';
    const title = 'Overall Status';

    const subTitleForExport = `${title}, ${prepareVersionSelectTitle()}`;

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle: subTitleForExport, title });
    }, [widgetId, subTitleForExport, title]);

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={title} />}
            id={overallStatusId}
            titleComponents={[
                <span
                    style={{ position: 'relative', bottom: '5px' }}
                    key={'subTitle'}
                    className={classes.subProjectTitle}
                >
                    {subTitle}
                </span>,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    widgetId={widgetId}
                    route={route}
                    projectId={null}
                    contractId={null}
                    filters={filters}
                    shouldHide={true}
                />,
                <ExportDropdown key={'export'} title={title} subTitle={subTitleForExport} />,
                <TooltipIcon key={'info'} tooltip={overAllStatus?.info} />,
                <ThreeDotsMenu
                    widgetViewMode={widgetViewMode}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    shareTitle={title}
                    shareLink={route}
                />,
            ]}
            titleFilters={[editNarrative && <div key={'editNarrative'}>{editNarrative}</div>]}
        >
            <div className={classes.widgetCardsWrapper}>
                {(isLoadingoverAllStatus || isFetchingoverAllStatus) && (
                    <LoaderContainer height={'500px'}>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!isLoadingoverAllStatus && !overAllStatusData && <NoData />}
                {!isLoadingoverAllStatus &&
                    overAllStatusData &&
                    Object.entries<IPortfolioTopHeroesItems[]>(overAllStatusData).map(([key, value]) => {
                        const titleText = key.slice(2);
                        return (
                            <WidgetCard
                                id={`overallStatusCard_${key}`}
                                contentHeight={'100%'}
                                titleText={titleText}
                                key={key}
                            >
                                {sortBy(value, ['order']).map((item, index) => {
                                    return (
                                        <CardItem
                                            key={`overallStatus-${item.name}`}
                                            title={capitalizeFirstLetter(item.name.toLowerCase())}
                                            showTooltip={false}
                                            showCardInfoTooltip={false}
                                            id={`overallStatusCardItem_title_${key}_${index}`}
                                        >
                                            <CardItemProgressBar
                                                id={`overallStatusCardItem_value_${key}_${index}`}
                                                mainText={item.value}
                                                currentValue={getCardItemValue(item.value)}
                                                textColor={getColorFromStatus(item.name, titleText)}
                                                maxValue={overAllStatus?.numOfItems}
                                            />
                                        </CardItem>
                                    );
                                })}
                            </WidgetCard>
                        );
                    })}
            </div>
        </WidgetWithTitle>
    );
};
